const MAX_PRODUCTS = 4;
const MAX_QUERY_LENGTH = 900;

class ArticleProducts extends HTMLElement {
  connectedCallback() {
    if (this.initialised) return;

    this.initialised = true;
    this.abortController = new AbortController();

    this.productGrid = this.querySelector('[data-product-grid]');
    this.tagsElement = this.querySelector('[data-article-tags]');

    const configuredLimit = Number.parseInt(
      this.dataset.limit || String(MAX_PRODUCTS),
      10
    );

    this.productLimit = Math.min(
      Math.max(configuredLimit || MAX_PRODUCTS, 1),
      MAX_PRODUCTS
    );

    if (!this.productGrid || !this.tagsElement) {
      this.hideSection();
      return;
    }

    this.loadProducts();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.initialised = false;
  }

  /**
   * Recommendation priority:
   *
   * 1. Article tags matching product tags.
   * 2. Article tags matching product vendors.
   * 3. Products from the selected fallback collection.
   */
  async loadProducts() {
    const articleTags = this.readArticleTags();

    this.setAttribute('aria-busy', 'true');

    try {
      /*
       * Only attempt automatic search when the article
       * has at least one usable tag.
       */
      if (articleTags.length > 0) {
        try {
          const tagProducts = await this.findProducts(
            'tag',
            articleTags
          );

          if (tagProducts.length > 0) {
            this.showProducts(tagProducts, 'tag');
            return;
          }

          const vendorProducts = await this.findProducts(
            'vendor',
            articleTags
          );

          if (vendorProducts.length > 0) {
            this.showProducts(vendorProducts, 'vendor');
            return;
          }
        } catch (searchError) {
          /*
           * A search error should not prevent the fallback
           * collection from being attempted.
           */
          if (searchError?.name === 'AbortError') {
            return;
          }

          console.warn(
            '[Article products] Automatic matching failed. Trying fallback collection.',
            searchError
          );
        }
      }

      const fallbackProducts = await this.fetchFallbackProducts();

      if (fallbackProducts.length > 0) {
        this.showProducts(fallbackProducts, 'fallback');
        return;
      }

      this.hideSection();
    } catch (error) {
      if (error?.name === 'AbortError') {
        return;
      }

      console.warn(
        '[Article products] Recommended products could not be loaded.',
        error
      );

      this.hideSection();
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  /**
   * Read, clean and deduplicate article tags.
   */
  readArticleTags() {
    try {
      const parsedTags = JSON.parse(
        this.tagsElement.textContent.trim() || '[]'
      );

      if (!Array.isArray(parsedTags)) {
        return [];
      }

      const tags = [];
      const usedTags = new Set();

      for (const value of parsedTags) {
        const tag = String(value || '').trim();

        if (!tag) continue;

        const comparisonTag = tag.toLocaleLowerCase();

        if (usedTags.has(comparisonTag)) continue;

        usedTags.add(comparisonTag);
        tags.push(tag);
      }

      return tags;
    } catch (error) {
      console.warn(
        '[Article products] Article tags could not be parsed.',
        error
      );

      return [];
    }
  }

  /**
   * Search query batches until four unique products
   * have been found.
   */
  async findProducts(field, values) {
    const queries = this.buildQueries(field, values);
    const products = [];
    const productIds = new Set();

    for (const query of queries) {
      const items = await this.fetchSearchProducts(query);

      for (const item of items) {
        const productId = item.dataset.productId;

        if (!productId || productIds.has(productId)) {
          continue;
        }

        if (!this.isValidProductItem(item)) {
          continue;
        }

        productIds.add(productId);
        products.push(item);

        if (products.length >= this.productLimit) {
          return products;
        }
      }
    }

    return products;
  }

  /**
   * Create queries such as:
   *
   * (tag:"Espresso" OR tag:"Coffee beans")
   */
  buildQueries(field, values) {
    const clauses = values.map((value) => {
      const escapedValue = this.escapeSearchValue(value);

      return `${field}:"${escapedValue}"`;
    });

    const queries = [];
    let currentBatch = [];

    for (const clause of clauses) {
      const proposedBatch = [...currentBatch, clause];
      const proposedQuery = proposedBatch.join(' OR ');

      if (
        currentBatch.length > 0 &&
        proposedQuery.length > MAX_QUERY_LENGTH
      ) {
        queries.push(`(${currentBatch.join(' OR ')})`);
        currentBatch = [clause];
      } else {
        currentBatch = proposedBatch;
      }
    }

    if (currentBatch.length > 0) {
      queries.push(`(${currentBatch.join(' OR ')})`);
    }

    return queries;
  }

  /**
   * Escape characters used by Shopify's search syntax.
   */
  escapeSearchValue(value) {
    return String(value)
      .replace(/\\/g, '\\\\')
      .replace(/"/g, '\\"')
      .replace(/:/g, '\\:')
      .replace(/\(/g, '\\(')
      .replace(/\)/g, '\\)');
  }

  /**
   * Fetch products matching an article tag or vendor.
   */
  async fetchSearchProducts(query) {
    const fallbackRoot = window.Shopify?.routes?.root || '/';

    const configuredSearchUrl =
      this.dataset.searchUrl || `${fallbackRoot}search`;

    const sectionName =
      this.dataset.searchSection || 'art-prod-search';

    const searchUrl = new URL(
      configuredSearchUrl,
      window.location.origin
    );

    searchUrl.searchParams.set('q', query);
    searchUrl.searchParams.set('type', 'product');
    searchUrl.searchParams.set('options[prefix]', 'none');
    searchUrl.searchParams.set(
      'options[unavailable_products]',
      'hide'
    );
    searchUrl.searchParams.set('sort_by', 'relevance');
    searchUrl.searchParams.set('section_id', sectionName);

    return this.fetchSectionProducts(searchUrl);
  }

  /**
   * Fetch products from the selected fallback collection.
   *
   * The request is made against the collection URL so
   * Liquid receives the correct collection context.
   */
  async fetchFallbackProducts() {
    const fallbackUrl = this.dataset.fallbackUrl?.trim();

    if (!fallbackUrl) {
      return [];
    }

    const sectionName =
      this.dataset.fallbackSection || 'art-prod-fallback';

    const collectionUrl = new URL(
      fallbackUrl,
      window.location.origin
    );

    collectionUrl.searchParams.set('section_id', sectionName);

    return this.fetchSectionProducts(collectionUrl);
  }

  /**
   * Fetch and parse a Shopify-rendered product section.
   */
  async fetchSectionProducts(url) {
    const response = await fetch(url.toString(), {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'default',
      signal: this.abortController.signal,
      headers: {
        Accept: 'text/html',
        'X-Requested-With': 'XMLHttpRequest'
      }
    });

    if (!response.ok) {
      throw new Error(
        `Shopify section request returned HTTP ${response.status}.`
      );
    }

    const responseHtml = await response.text();

    const responseDocument = new DOMParser().parseFromString(
      responseHtml,
      'text/html'
    );

    this.installStyles(responseDocument);

    const responseList = responseDocument.querySelector(
      '[data-product-response]'
    );

    if (!responseList) {
      return [];
    }

    return Array.from(responseList.children)
      .filter((item) => {
        return (
          item instanceof HTMLElement &&
          item.hasAttribute('data-product-item')
        );
      })
      .map((item) => document.importNode(item, true))
      .filter((item) => this.isValidProductItem(item))
      .slice(0, this.productLimit);
  }

  /**
   * Copy dynamically returned Horizon styles onto
   * the current article page.
   */
  installStyles(responseDocument) {
    const styles = responseDocument.querySelectorAll('style');

    for (const style of styles) {
      const css = style.textContent?.trim();

      if (!css) continue;

      const styleKey = this.hashString(css);

      const existingStyle = document.head.querySelector(
        `style[data-article-product-style="${styleKey}"]`
      );

      if (existingStyle) continue;

      const installedStyle = document.createElement('style');

      installedStyle.dataset.articleProductStyle = styleKey;
      installedStyle.textContent = css;

      document.head.appendChild(installedStyle);
    }
  }

  /**
   * Create a deterministic stylesheet identifier.
   */
  hashString(value) {
    let hash = 0;

    for (let index = 0; index < value.length; index += 1) {
      hash = (
        hash * 31 +
        value.charCodeAt(index)
      ) >>> 0;
    }

    return hash.toString(36);
  }

  /**
   * Ensure the item contains a populated Horizon card.
   */
  isValidProductItem(item) {
    if (!(item instanceof HTMLElement)) {
      return false;
    }

    if (!item.dataset.productId) {
      return false;
    }

    const card = item.querySelector(
      'product-card, .product-card'
    );

    if (!card) {
      return false;
    }

    const hasLink = Boolean(card.querySelector('a[href]'));
    const hasImage = Boolean(card.querySelector('img'));
    const hasText = card.textContent.trim().length > 0;

    return hasLink || hasImage || hasText;
  }

  /**
   * Display automatic or fallback products.
   */
  showProducts(items, source) {
    const validItems = items
      .filter((item) => this.isValidProductItem(item))
      .slice(0, this.productLimit);

    if (validItems.length === 0) {
      this.hideSection();
      return;
    }

    this.productGrid.replaceChildren(...validItems);
    this.dataset.source = source;
    this.hidden = false;
  }

  /**
   * Hide the heading and grid when no products exist.
   */
  hideSection() {
    this.dataset.source = 'none';
    this.hidden = true;

    if (this.productGrid) {
      this.productGrid.replaceChildren();
    }
  }
}

if (!customElements.get('article-products')) {
  customElements.define('article-products', ArticleProducts);
}